import { Component } from '@angular/core';
import { MainFrontendConfigFieldAccess } from 'src/modules/app-template/netgen/MainFrontendConfigFieldAccess';
import { DatabaseUserAttribute } from 'src/modules/database/shared/database-user-attribute.model';
import { DatabaseUserGroup } from 'src/modules/database/shared/database-user-group.model';
import { DatabaseUser } from 'src/modules/database/shared/database-user.entity';
import { RestEndpoint } from 'src/modules/sm-base/models/rest-endpoint.model';
import { FrontendField } from 'src/modules/sm-base/netgen/FrontendField';
import { FrontendFieldType } from 'src/modules/sm-base/netgen/FrontendFieldType';
import { FrontendForm } from 'src/modules/sm-base/netgen/FrontendForm';
import { Objects } from "src/modules/utils/shared/objects";
import { Utils } from 'src/modules/utils/shared/utils';
import { FrontendFieldListItem } from '../../netgen/FrontendFieldListItem';
import { AdminBaseEditComponent } from '../admin-base-edit/admin-base-edit.component';
import { EditGeneralUsersComponent } from '../edit-general-users/edit-general-users.component';

@Component({
    selector: 'app-edit-general-user',
    templateUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.html',
    styleUrl: '../../../sm-base/components/admin-base-edit/admin-base-edit.component.scss',
    standalone: false
})
export class EditGeneralUserComponent extends AdminBaseEditComponent<DatabaseUser> {
    
    definition = Objects.with(EditGeneralUsersComponent.staticDefinition, "canInsert", this.app.mainConfig.adminUsersAllowAdd);

    userGroups: DatabaseUserGroup[];
    
    async initAdditionalData(): Promise<void> {
        this.userGroups = await this.app.loadDataHandler(async () => RestEndpoint.main().run("api/app/usergroup").list(DatabaseUserGroup), this);
        (this.item as any).groups = this.item.groups.map(g => g.id);      
    }

    getForm(): FrontendForm {
        return new FrontendForm([
            this.applyFieldAccess(new FrontendField("userName", "Benutzername", FrontendFieldType.string, { mandatory: true})),
            this.applyFieldAccess(new FrontendField("firstName", "Vorname", FrontendFieldType.string)),
            this.applyFieldAccess(new FrontendField("lastName", "Nachname", FrontendFieldType.string)),
            this.applyFieldAccess(new FrontendField("mail", "E-Mail", FrontendFieldType.string, { isEmailAddress: true})),
            this.applyFieldAccess(new FrontendField("phone", "Telefon", FrontendFieldType.string)),
            this.applyFieldAccess(new FrontendField("isAdmin", "Ist Administrator", FrontendFieldType.bool)),
            this.applyFieldAccess(new FrontendField("active", "Benutzer aktiv", FrontendFieldType.bool), MainFrontendConfigFieldAccess.hidden),
            this.applyFieldAccess(new FrontendField("color", "Farbe", FrontendFieldType.color, {
                predefinedColors: Utils.getMaterialColors()
            }), MainFrontendConfigFieldAccess.hidden),
            this.app.mainConfig.useAdminUserGroups ? this.app.mainConfig.onlyOneGroupPerUser ?  new FrontendField("groups", "Gruppenzugehörigkeit", FrontendFieldType.dropdown, { 
                listItems: FrontendFieldListItem.make(this.userGroups, ug => [ug.id, ug.name], true),
                dropdownCanFilter: true,
                toForm: v => Utils.isNoe(v) ? null : v[0],
                fromForm: v => v == null ? [] : [v]
            }) : new FrontendField("groups", "Gruppenzugehörigkeit", FrontendFieldType.dropdownMulti, { 
                dropdownEditable: true, 
                listItems: FrontendFieldListItem.make(this.userGroups, ug => [ug.id, ug.name], true)
            }) : null,
            this.applyFieldAccess(new FrontendField("password", this.id > 0 ? "Passwort (eintippen für PW-Wechsel)" : "Passwort", FrontendFieldType.password, { mandatory: this.isInsert() })),
            ...this.app.mainConfig.additionalUserAttributes.map(a => {
                let result = Objects.cloneDeep(a);
                result.id = "attrib_" + result.id;
                return result;
            }),
            this.app.mainConfig.customer == "lemgo" ? new FrontendField("generateLetter", "Anschreiben erzeugen", FrontendFieldType.button, {
                onClick: this.customerLemgoGenerateLetter.bind(this)
            }) : null
        ]);
    }

    async fillForm(): Promise<void> {
        await super.fillForm();
        for (let attrib of this.app.mainConfig.additionalUserAttributes) {
            let a = this.item.attributes.find(a2 => a2.key == attrib.id);
            if (a != null) {
                let field = this.form.getField("attrib_" + attrib.id);
                let v = field.type == FrontendFieldType.bool ? Utils.toBool(a.value) : a.value;
                field.setValue(v);
            }
        }
    }

    async fromForm(): Promise<void> {
        await super.fromForm();

        this.item.groups = ((this.item as any).groups as number[]).map(id => this.userGroups.find(ug => ug.id == id));

        if (!Utils.isNoe((this.item as any).password)) {
            if (this.id > 0 && !await this.app.dialog.yesNo("Soll das Passwort des Benutzers geändert werden?", "Warnung")) {
                return;
            }
            (this.item as any).attrib____passwordChange = (this.item as any).password;
        }

        for (let attrib of [...this.app.mainConfig.additionalUserAttributes, new FrontendField("___passwordChange", "", FrontendFieldType.string)]) {
            if (Objects.hasProperty(this.item, "attrib_" + attrib.id)) {
                let a = this.item.attributes.find(a2 => a2.key == attrib.id);
                if (a == null) {
                    a = Objects.fromPlain(DatabaseUserAttribute, {
                        key: attrib.id
                    });
                    this.item.attributes.push(a);
                }
                a.value = this.item["attrib_" + attrib.id];
                delete this.item["attrib_" + attrib.id];
            }
        }
    }

    
    private applyFieldAccess(field: FrontendField, defaultAccess = MainFrontendConfigFieldAccess.editable): FrontendField {
        let fa = this.app.mainConfig.adminUsersFieldAccess;
        if (fa == null) {
            return field;
        }
        if (Objects.getSafe(fa, field.id, defaultAccess) == MainFrontendConfigFieldAccess.hidden) {
            return null;
        }
        if (Objects.getSafe(fa, field.id, defaultAccess) == MainFrontendConfigFieldAccess.readOnly) {
            field.enabled = false;
        }
        return field;
    }

    private async customerLemgoGenerateLetter(): Promise<void> {
        await RestEndpoint.main().query({ type: "generateLetter" }).post().body({ userName: this.item.userName }).runSaveAs("api/app/customerCall", "PortalLogin.pdf");
    }
   
}